import { Spacing } from '@persona-id/design-tokens';
import { noop } from 'lodash';
import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { BOX_SHADOWS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import { Z_INDICES } from 'dashboard/styles/variables';
import { Paragraph } from 'design-system/components/Text';
import { paragraphMixins } from 'design-system/mixins/text';
import { Elevation } from 'design-system/variables/effect';
import { useEscListener } from 'lib/hooks/useKeyListener';

const RouteTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: top;
  ${paragraphMixins.lgBold};
`;

const RouteTitle = styled.div`
  align-self: center;
`;

const RouteSubtitle = styled.div`
  margin-top: ${Spacing[1]};
`;

const HeaderPaddedContainer = styled.div<{ showHairlines: boolean }>`
  padding: ${Spacing[3]} ${Spacing[3]} ${Spacing[0]};
  ${({ showHairlines, theme }) =>
    showHairlines &&
    css`
      border-bottom: 1px solid ${theme.color.border.secondary};
      padding: ${Spacing[3]};
    `}
`;

const ScrollingContainer = styled.div<{ showHairlines: boolean }>`
  flex: 1;
  overflow: auto;
  height: 100%;
  padding: ${Spacing[0]} ${Spacing[3]};
  ${({ showHairlines }) =>
    !showHairlines &&
    css`
      margin: ${Spacing[3]} ${Spacing[0]};
    `}
`;

const StyledContainer = styled.div<{
  blockedLeft: string;
  blockedRight: string;
  panelPosition: 'right' | 'left';
  getContainerStyle?: UpdateStyle;
  disableExpand?: boolean;
}>`
  box-sizing: border-box;

  position: fixed;
  width: ${({ blockedLeft, blockedRight }) =>
    `min(800px, max(100vw - ${blockedRight}, ${blockedLeft}))`};
  ${({ disableExpand }) =>
    disableExpand &&
    css`
      width: auto;
    `}
  max-height: 550px;
  min-height: 400px;

  ${({ panelPosition, blockedLeft }) =>
    panelPosition === 'left' &&
    css`
      left: ${blockedLeft};
    `}
  ${({ panelPosition, blockedLeft, blockedRight }) =>
    panelPosition === 'right' &&
    css`
      right: min(${blockedRight}, 100vw - ${blockedLeft});
    `}
  top: calc(50vh - 200px);

  z-index: ${Z_INDICES.modal};
  box-shadow: ${Elevation.High};
  border-radius: ${(p) => p.theme.borderRadius};
  background-color: ${({ theme }) => theme.color.background.primary};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${({ getContainerStyle, blockedLeft, blockedRight }) =>
    getContainerStyle && getContainerStyle({ blockedLeft, blockedRight })}
`;

const StyledFooter = styled.div<{ showShadow: boolean; showHairlines: boolean }>`
  position: sticky;
  bottom: 0;
  width: 100%;
  padding: ${Spacing[3]};
  ${({ showHairlines, theme }) =>
    showHairlines &&
    css`
      border-top: 1px solid ${theme.color.border.secondary};
    `}
  box-shadow: ${({ showShadow }) => showShadow && BOX_SHADOWS.card};
  background-color: ${({ theme }) => theme.color.background.primary};
  z-index: 1;
`;

type UpdateStyle = (props: { blockedLeft: string; blockedRight: string }) => ReturnType<typeof css>;

interface PositionedPanelProps {
  title: string | React.ReactNode;
  offsetElementId?: string;
  panelPosition?: 'right' | 'left';
  blockedLeft?: string;
  blockedRight?: string;
  outsideExceptionalIdPrefixes?: string[]; // ID prefixes to prevent from treating as clicking outside
  getContainerStyle?: UpdateStyle;
  'aria-expanded'?: boolean;
  onOutsideClickAndEscPress?: () => void;
  rightButton?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  disableExpand?: boolean;
  subtitle?: string;
  showHairlines?: boolean;
}

const PositionedPanel: FC<PositionedPanelProps> = (props: PositionedPanelProps) => {
  const {
    title,
    subtitle,
    offsetElementId = 'workflow-control-panel__container',
    onOutsideClickAndEscPress = noop,
    rightButton,
    outsideExceptionalIdPrefixes = [],
    getContainerStyle,
    'aria-expanded': ariaExpanded = false,
    footer,
    children,
    disableExpand,
    panelPosition = 'right',
    showHairlines = false,
  } = props;

  const blockedLeft =
    props.blockedLeft ?? (document.getElementById('navbar__container')?.clientWidth || 0) + 'px';
  const blockedRight =
    props.blockedRight ?? (document.getElementById(offsetElementId)?.clientWidth || 0) + 'px';

  const isOutsideClick = useCallback(
    (e: MouseEvent): boolean => {
      if (outsideExceptionalIdPrefixes.length === 0) {
        return true;
      }
      const selectors = outsideExceptionalIdPrefixes.map((prefix) => `[id^="${prefix}"]`).join(',');
      return !Array.from(document.querySelectorAll(selectors)).some(
        (reactSelectOption) => e.target instanceof Node && reactSelectOption.contains(e.target),
      );
    },
    [outsideExceptionalIdPrefixes],
  );

  useEscListener(onOutsideClickAndEscPress);

  const containerRef = useRef<HTMLDivElement>(null);
  const [showShadow, setShowShadow] = useState(false);

  type ShadowCompareAttributes = {
    scrollHeight: number;
    clientHeight: number;
    scrollTop: number;
  };

  const isAtScrollEnd = ({
    scrollHeight,
    scrollTop,
    clientHeight,
  }: ShadowCompareAttributes): boolean => scrollHeight - scrollTop === clientHeight;

  const conditionallyShowShadow = (element: ShadowCompareAttributes): void => {
    element.scrollHeight > element.clientHeight && setShowShadow(!isAtScrollEnd(element));
  };

  useEffect(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    containerRef != null && conditionallyShowShadow(containerRef.current);
  });

  return (
    <OutsideClickHandler
      onOutsideClick={(e) => {
        if (isOutsideClick(e)) {
          onOutsideClickAndEscPress();
        }
      }}
    >
      <StyledContainer
        panelPosition={panelPosition}
        ref={containerRef}
        aria-expanded={ariaExpanded}
        getContainerStyle={getContainerStyle}
        aria-label='Hovered Card'
        blockedLeft={blockedLeft}
        blockedRight={blockedRight}
        disableExpand={disableExpand}
        onScroll={(e) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          conditionallyShowShadow(e.target);
        }}
      >
        <HeaderPaddedContainer showHairlines={showHairlines}>
          <RouteTitleRow>
            <RouteTitle>{title}</RouteTitle>
            {rightButton}
          </RouteTitleRow>
          {subtitle && (
            <RouteSubtitle>
              <Paragraph>{subtitle}</Paragraph>
            </RouteSubtitle>
          )}
        </HeaderPaddedContainer>
        <ScrollingContainer showHairlines={showHairlines}>{children}</ScrollingContainer>
        {footer && (
          <StyledFooter showShadow={showShadow} showHairlines={showHairlines}>
            {footer}
          </StyledFooter>
        )}
      </StyledContainer>
    </OutsideClickHandler>
  );
};

export default PositionedPanel;
